import { useIsMobile } from '@/hooks/use-is-mobile';
import { CardSkeleton } from '@rhg/components/ui/skeletons';
import { Box, SxProps, Theme } from '@rhg/libs/material';
import { ReactNode, useEffect, useRef, useState } from 'react';

export function PackPageSkeleton() {
    const isMobile = useIsMobile('md');
    /*
     * These widths are specific to /discover/pack/calmhealth-all-sleep?viewAll=true.
     * We can iterate as we try to use this in more places
     */
    const imageWidth = isMobile ? 164 : 326;
    return (
        <CardSkeleton
            variant="clinical"
            imageWidth={imageWidth}
            cardHeight="100%"
            cardWidth="100%"
            imageAspectRatio="1"
            imageHeight="auto"
            contentHeight="107px"
        />
    );
}

export function PlaceholderWhileOffScreen({
    children,
    placeholder,
    viewports = 2,
    unrenderOffscreen,
    sx,
}: {
    children: ReactNode;
    placeholder: ReactNode;
    viewports?: number;
    unrenderOffscreen?: boolean;
    sx?: SxProps<Theme>;
}) {
    const [isVisible, setIsVisible] = useState(false);
    const elementRef = useRef(null);

    useEffect(() => {
        const viewportHeight = window.innerHeight;
        const rootMargin = `${viewports * viewportHeight}px`; // Preload content when it's within `viewports` multiple of the viewport height
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !isVisible) {
                        setIsVisible(true);
                    } else if (
                        unrenderOffscreen &&
                        !entry.isIntersecting &&
                        isVisible
                    ) {
                        setIsVisible(false);
                    }
                });
            },
            {
                root: null, // Use the viewport as the root
                rootMargin,
                threshold: 0, // Trigger as soon as any part of the element intersects
            }
        );

        const currentElement = elementRef.current;
        if (currentElement) {
            observer.observe(currentElement);
        }

        return () => {
            if (currentElement) {
                observer.unobserve(currentElement);
            }
        };
    }, [isVisible, unrenderOffscreen, viewports]);

    return (
        <Box ref={elementRef} sx={sx}>
            {isVisible ? children : placeholder}
        </Box>
    );
}
